/*
 * Scentify — Build Your Starter Kit v5 control: guided quantity + mix & match
 *
 * Behaviour is a port of the BTS prototype (demo.burntheships.ai/scentify-v2/):
 * three panels driven by a single sticky CTA, scent family filters, a detail
 * drawer, and per-step sticky label/note swaps.
 *
 * Cart contract is deliberately identical to component-build-your-starter-kit.js
 * so the money path does not differ between arms:
 *   item 1 = diffuser variant (qty), no selling plan
 *   items 2+ = one line per picked scent variant, quantity = bottles using
 *              it (a comped bottle per diffuser, mix-and-match per bottle),
 *              selling plan attached only for subscription
 * then hand off to /checkout.
 *
 * Pricing mirrors the live control: the diffuser carries the percentage
 * discount, the scent is comped, and the scent always carries a refill
 * selling plan (subscription-only, matching the control).
 *
 * IMPORTANT: figures are DISPLAY only. The actual reduction is applied by
 * Shopify (automatic discount / function) at checkout. See PR #36.
 *
 * All product, price, inventory and plan data is injected by
 * sections/main-bysk-v5-control.liquid as window.BYSKV2_DATA_V5_CONTROL.
 * Nothing is hardcoded —
 * inventory follows Shopify.
 */
(function () {
  'use strict';

  var DATA = window.BYSKV2_DATA_V5_CONTROL;
  if (!DATA) return;

  function money(cents) {
    var whole = cents % 100 === 0;
    return (
      DATA.currency_symbol +
      (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      })
    );
  }

  /* Mirrors the section's min_scent: the kit-list FREE value before a scent
     is chosen shows the cheapest bottle, like the server-rendered row. */
  function minScentPrice() {
    var m = 0;
    for (var i = 0; i < DATA.scents.length; i++) {
      if (!m || DATA.scents[i].price < m) m = DATA.scents[i].price;
    }
    return m;
  }

  function fill(pattern, scent, value, n) {
    return String(pattern || '')
      .replace('{scent}', scent)
      .replace('{value}', value)
      .replace('{n}', n === undefined ? '' : n);
  }

  /* Funnel step names — shared verbatim with the control arm
     (component-build-your-starter-kit-multi.js) so one Mixpanel funnel can be
     built on bysk_step_viewed and broken down by `arm`. Never rename these
     without changing both files: the funnel is defined on these strings. */
  var STEP_NAMES = { 1: 'diffuser', 2: 'scent', 3: 'review' };

  function BYSKv2(root) {
    this.root = root;
    this.state = {
      step: 1,
      maxStep: 1,
      qty: 1,
      diffuser_variant: null,
      /* one free fragrance per BOTTLE (index < qty) */
      scents: [],
      /* which bottle step 2 is currently picking for */
      picking: 0,
      weeks: String(DATA.default_weeks || ''),
      fam: 'all'
    };
    this.drawerScent = null;
    this.init();
  }

  BYSKv2.prototype.init = function () {
    this.cacheDom();
    this.selectFirstAvailableDiffuser();
    this.bindProgress();
    this.bindDiffuser();
    this.bindQty();
    this.bindFilters();
    this.bindScents();
    this.bindDrawer();
    this.bindSchedule();
    this.bindStickyCta();
    this.bindKitScentShortcut();
    this.restoreLandingFragrance();
    this.render();

    /* Step 1 is rendered, never navigated to, so it emitted nothing — the
       funnel had no denominator and step-1 -> step-2 drop-off was
       unmeasurable. Fire it on entry, like any other step. */
    this.track('bysk_step_viewed', this.stepProps(1, 'entry'));
  };

  /* Only rendered by the 'action' treatment of the kit list. */
  BYSKv2.prototype.bindKitScentShortcut = function () {
    var self = this;
    if (!this.dom.stackScentGo) return;
    this.dom.stackScentGo.addEventListener('click', function () {
      self.track('bysk_kit_scent_shortcut_clicked', {});
      self.goToStep(2);
    });
  };

  /* Carry an eligible landing-page choice into bottle one without skipping
     the diffuser or refill review steps. Unknown/unavailable choices are ignored. */
  BYSKv2.prototype.restoreLandingFragrance = function () {
    if (!window.location || !window.location.search) return;
    var handle = new URLSearchParams(window.location.search).get('scent');
    if (!handle) return;
    for (var i = 0; i < DATA.scents.length; i++) {
      var scent = DATA.scents[i];
      if (scent.handle === handle && this.scentEligible(scent)) {
        this.state.scents[0] = scent;
        return;
      }
    }
  };

  BYSKv2.prototype.cacheDom = function () {
    var r = this.root;
    this.dom = {
      panels: r.querySelectorAll('.step-panel'),
      psteps: r.querySelectorAll('.pstep'),
      heroImg: r.querySelector('[data-hero-img]'),
      thumbs: r.querySelectorAll('[data-thumb-row] [data-img]'),
      diffuserOpts: r.querySelectorAll('[data-diffuser-variant]'),
      scentCards: r.querySelectorAll('[data-scent-variant]'),
      kitPicker: r.querySelector('[data-kit-picker]'),
      step2HeadingText: r.querySelector('[data-step2-heading-text]'),
      freeNote: r.querySelector('[data-free-note]'),
      sumScents: r.querySelector('[data-sum-scents]'),
      schedPills: r.querySelectorAll('[data-sched-pills] .spill'),
      refillPrice: r.querySelector('[data-refill-price]'),
      qtyInc: r.querySelector('[data-qty-inc]'),
      qtyDec: r.querySelector('[data-qty-dec]'),
      qtyVal: r.querySelector('[data-qty-val]'),
      filters: r.querySelectorAll('[data-filters] .fpill'),
      stackDiffT: r.querySelector('[data-stack-diff-t]'),
      stackDiffV: r.querySelector('[data-stack-diff-v]'),
      stackDiffImg: r.querySelector('[data-stack-diff-img]'),
      stackScentRow: r.querySelector('[data-stack-scent-row]'),
      stackScentName: r.querySelector('[data-stack-scent-name]'),
      stackScentD: r.querySelector('[data-stack-scent-d]'),
      stackScentImg: r.querySelector('[data-stack-scent-img]'),
      stackScentV: r.querySelector('[data-stack-scent-v]'),
      stackScentGo: r.querySelector('[data-stack-scent-go]'),
      stackTotal: r.querySelector('[data-stack-total]'),
      savePill: r.querySelector('[data-save-pill]'),
      sumValueTag: r.querySelector('[data-sum-value-tag]'),
      sumDiffImg: r.querySelector('[data-sum-diff-img]'),
      sumDiffT: r.querySelector('[data-sum-diff-t]'),
      sumDiffV: r.querySelector('[data-sum-diff-v]'),
      sumScentImg: r.querySelector('[data-sum-scent-img]'),
      sumScentT: r.querySelector('[data-sum-scent-t]'),
      sumScentD: r.querySelector('[data-sum-scent-d]'),
      sumScentV: r.querySelector('[data-sum-scent-v]'),
      sumTotal: r.querySelector('[data-sum-total]'),
      sumSavePill: r.querySelector('[data-sum-save-pill]'),
      stickyRoot: r.querySelector('.sticky-cta'),
      stickyLbl: r.querySelector('[data-sticky-lbl]'),
      stickyWas: r.querySelector('[data-sticky-was]'),
      stickyNow: r.querySelector('[data-sticky-now]'),
      stickyCta: r.querySelector('[data-sticky-cta]'),
      stickyNote: r.querySelector('[data-sticky-note]'),
      drawer: r.querySelector('[data-drawer]'),
      drawerVeil: r.querySelector('[data-drawer-veil]'),
      drawerClose: r.querySelector('[data-drawer-close]'),
      drawerTitle: r.querySelector('[data-drawer-title]'),
      drawerImg: r.querySelector('[data-drawer-img]'),
      drawerInsp: r.querySelector('[data-drawer-insp]'),
      drawerDesc: r.querySelector('[data-drawer-desc]'),
      drawerTop: r.querySelector('[data-drawer-top]'),
      drawerMid: r.querySelector('[data-drawer-mid]'),
      drawerBase: r.querySelector('[data-drawer-base]'),
      drawerPick: r.querySelector('[data-drawer-pick]')
    };
  };

  /* The prototype hardcoded stock (Black diffuser and White Tea permanently
     sold out while both were live). Never assume — read the variant. */
  BYSKv2.prototype.selectFirstAvailableDiffuser = function () {
    for (var i = 0; i < DATA.diffuser.variants.length; i++) {
      if (DATA.diffuser.variants[i].available) {
        this.state.diffuser_variant = DATA.diffuser.variants[i];
        break;
      }
    }
  };

  BYSKv2.prototype.findScent = function (id) {
    for (var i = 0; i < DATA.scents.length; i++) {
      if (String(DATA.scents[i].id) === String(id)) return DATA.scents[i];
    }
    return null;
  };

  /* A Starter Kit scent must be in stock AND carry the selected refill plan.
     The discount function comps the scent (and takes 40% off the diffuser)
     only when the scent line carries a selling plan, so an in-stock variant
     whose plans the section couldn't read must never be pickable — it would
     cart at full price with both discounts silently skipped (Tokyo Nights and
     Demure, whose Recharge plans are named differently from the other 18). */
  BYSKv2.prototype.scentEligible = function (scent) {
    return !!(scent && scent.available && this.sellingPlanFor(scent));
  };

  /* ------------- multi-bottle helpers (ported from v4) ------------- */

  BYSKv2.prototype.pickedScents = function () {
    return this.state.scents.slice(0, this.state.qty).filter(Boolean);
  };

  BYSKv2.prototype.allScentsPicked = function () {
    var n = this.state.qty;
    for (var i = 0; i < n; i++) if (!this.state.scents[i]) return false;
    return n > 0;
  };

  /* First bottle without a fragrance, or -1 when complete. */
  BYSKv2.prototype.nextUnpicked = function () {
    var n = this.state.qty;
    for (var i = 0; i < n; i++) if (!this.state.scents[i]) return i;
    return -1;
  };

  /* Assign a fragrance to the bottle being picked for, then advance to the
     next one still missing — the multi-bottle flow is "tap, tap, continue". */
  BYSKv2.prototype.assignScent = function (scent, via) {
    if (!this.scentEligible(scent)) return;
    var slot = this.state.picking;
    if (slot >= this.state.qty) slot = Math.max(0, this.state.qty - 1);
    this.state.scents[slot] = scent;
    var open = this.nextUnpicked();
    if (open !== -1) this.state.picking = open;
    this.render();
    this.track('bysk_scent_selected', {
      scent: scent.title,
      variant_id: scent.id,
      kit_index: slot + 1,
      kit_count: this.state.qty,
      via: via
    });
  };

  /* Changing quantity invalidates picks for bottles that no longer exist. */
  BYSKv2.prototype.trimScentsToQty = function () {
    this.state.scents.length = Math.min(this.state.scents.length, this.state.qty);
    if (this.state.picking >= this.state.qty) {
      this.state.picking = Math.max(0, this.state.qty - 1);
    }
    var open = this.nextUnpicked();
    if (open !== -1) this.state.picking = open;
  };

  /* ---------------- navigation ---------------- */

  BYSKv2.prototype.bindProgress = function () {
    var self = this;
    Array.prototype.forEach.call(this.dom.psteps, function (ps) {
      ps.addEventListener('click', function () {
        var t = parseInt(ps.dataset.step, 10);
        if (t < self.state.step) self.goToStep(t);
      });
    });
  };

  /* Every property the step funnel needs, on every step event, so drop-off can
     be sliced without joining to other events. */
  BYSKv2.prototype.stepProps = function (step, direction) {
    var d = this.state.diffuser_variant;
    var scents = this.pickedScents();
    var t = this.totals();
    return {
      step: step,
      step_name: STEP_NAMES[step] || String(step),
      arm: DATA.fragrance_gift_cta === true ? 'fragrance-images' : 'control',
      fragrance_experiment: this.fragranceDecision ? 'bysk-fragrance-images' : null,
      direction: direction,
      /* Funnels should count first arrivals — back-nav re-enters a step. */
      is_first_view: step > this.state.maxStep,
      max_step_reached: Math.max(this.state.maxStep, step),
      diffuser_qty: this.state.qty,
      diffuser_variant_id: d ? d.id : null,
      diffuser_variant_title: d ? d.title : null,
      scent_variant_id: scents.length ? scents[0].id : null,
      scent_title: scents.map(function (x) { return x.title; }).join(' + ') || null,
      kit_total: t.total / 100
    };
  };

  BYSKv2.prototype.goToStep = function (step) {
    if (step === 2 && !this.state.diffuser_variant) return;
    if (step === 2 && !this.fragranceDecision && window.__scentifyAssignFragrance) {
      if (this.fragrancePending) return;
      this.fragrancePending = true;
      var self = this;
      window.__scentifyAssignFragrance(function (variation) {
        self.fragranceDecision = true;
        self.fragrancePending = false;
        DATA.fragrance_gift_cta = variation;
        self.root.setAttribute('data-fragrance-treatment', variation ? 'true' : 'false');
        if (variation) self.root.querySelectorAll('[data-fragrance-image]').forEach(function (img) {
          img.src = img.getAttribute('data-fragrance-image');
          img.removeAttribute('srcset');
          img.parentElement.classList.remove('simg--product');
        });
        self.goToStep(2);
      });
      return;
    }
    if (step === 3 && !this.allScentsPicked()) return;
    var direction = step > this.state.step ? 'forward' : 'back';
    var props = this.stepProps(step, direction);
    this.state.step = step;
    if (step > this.state.maxStep) this.state.maxStep = step;
    this.render();
    this.track('bysk_step_viewed', props);
    window.scrollTo({ top: 0, behavior: 'instant' });
    var shell = this.root.querySelector('[data-panel="' + step + '"] .shell');
    if (shell) {
      shell.classList.remove('fade-in');
      void shell.offsetWidth;
      shell.classList.add('fade-in');
    }
  };

  /* ---------------- step 1 ---------------- */

  BYSKv2.prototype.bindDiffuser = function () {
    var self = this;
    Array.prototype.forEach.call(this.dom.diffuserOpts, function (el) {
      el.addEventListener('click', function () {
        if (el.dataset.available !== 'true') return;
        for (var i = 0; i < DATA.diffuser.variants.length; i++) {
          if (String(DATA.diffuser.variants[i].id) === el.dataset.diffuserVariant) {
            self.state.diffuser_variant = DATA.diffuser.variants[i];
            break;
          }
        }
        self.render();
      });
    });
    Array.prototype.forEach.call(this.dom.thumbs, function (btn) {
      btn.addEventListener('click', function () {
        if (!self.dom.heroImg) return;
        self.dom.heroImg.src = btn.dataset.img;
        Array.prototype.forEach.call(self.dom.thumbs, function (b) {
          b.classList.remove('sel');
        });
        btn.classList.add('sel');
      });
    });
  };

  BYSKv2.prototype.bindQty = function () {
    var self = this;
    if (this.dom.qtyInc) {
      this.dom.qtyInc.addEventListener('click', function () {
        self.state.qty = Math.min(self.state.qty + 1, DATA.max_qty);
        self.trimScentsToQty();
        self.render();
      });
    }
    if (this.dom.qtyDec) {
      this.dom.qtyDec.addEventListener('click', function () {
        self.state.qty = Math.max(self.state.qty - 1, 1);
        self.trimScentsToQty();
        self.render();
      });
    }
  };

  /* ---------------- step 2 ---------------- */

  BYSKv2.prototype.bindFilters = function () {
    var self = this;
    Array.prototype.forEach.call(this.dom.filters, function (b) {
      b.addEventListener('click', function () {
        self.state.fam = b.dataset.fam;
        Array.prototype.forEach.call(self.dom.filters, function (x) {
          x.classList.toggle('on', x === b);
        });
        Array.prototype.forEach.call(self.dom.scentCards, function (card) {
          var show = self.state.fam === 'all' || card.dataset.fam === self.state.fam;
          card.style.display = show ? '' : 'none';
        });
      });
    });
  };

  BYSKv2.prototype.bindScents = function () {
    var self = this;
    Array.prototype.forEach.call(this.dom.scentCards, function (el) {
      /* Warm the drawer image on first hover so opening Details is instant. */
      el.addEventListener(
        'pointerenter',
        function () {
          var s = self.findScent(el.dataset.scentVariant);
          if (s && s.pack) new Image().src = s.pack;
        },
        { once: true }
      );
      el.addEventListener('click', function () {
        var scent = self.findScent(el.dataset.scentVariant);
        if (!self.scentEligible(scent)) return;
        self.assignScent(scent, 'grid');
      });
    });
  };

  BYSKv2.prototype.bindDrawer = function () {
    var self = this;
    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-scent-details]'),
      function (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          self.openDrawer(parseInt(btn.dataset.scentDetails, 10));
        });
      }
    );
    if (this.dom.drawerClose) {
      this.dom.drawerClose.addEventListener('click', function () { self.closeDrawer(); });
    }
    if (this.dom.drawerVeil) {
      this.dom.drawerVeil.addEventListener('click', function () { self.closeDrawer(); });
    }
    if (this.dom.drawerPick) {
      this.dom.drawerPick.addEventListener('click', function () {
        if (!self.scentEligible(self.drawerScent)) return;
        self.assignScent(self.drawerScent, 'drawer');
        self.closeDrawer();
      });
    }
  };

  BYSKv2.prototype.openDrawer = function (index) {
    var s = DATA.scents[index];
    if (!s) return;
    this.drawerScent = s;
    var d = this.dom;
    if (d.drawerTitle) d.drawerTitle.textContent = s.title;
    if (d.drawerImg) {
      /* Hide the previous scent's image until the new one has decoded —
         otherwise it stays visible for the beat the swap takes. The .dimg
         container keeps a 1:1 aspect so the drawer doesn't jump. */
      var next = s.pack || s.image || '';
      if (d.drawerImg.getAttribute('src') !== next) {
        d.drawerImg.classList.add('loading');
        d.drawerImg.onload = d.drawerImg.onerror = function () {
          d.drawerImg.classList.remove('loading');
        };
        d.drawerImg.src = next;
        if (d.drawerImg.complete && d.drawerImg.naturalWidth > 0) {
          d.drawerImg.classList.remove('loading');
        }
      }
    }
    if (d.drawerInsp) d.drawerInsp.textContent = s.insp ? DATA.labels.inspired_by + ' ' + s.insp : '';
    if (d.drawerDesc) d.drawerDesc.textContent = s.desc || '';
    if (d.drawerTop) d.drawerTop.textContent = s.top || '';
    if (d.drawerMid) d.drawerMid.textContent = s.mid || '';
    if (d.drawerBase) d.drawerBase.textContent = s.base || '';
    if (d.drawerPick) {
      var eligible = this.scentEligible(s);
      d.drawerPick.disabled = !eligible;
      d.drawerPick.textContent = eligible
        ? fill(DATA.labels.drawer_pick, s.title, money(s.price))
        : DATA.labels.drawer_sold;
    }
    if (d.drawer) d.drawer.classList.add('on');
    if (d.drawerVeil) d.drawerVeil.classList.add('on');
  };

  BYSKv2.prototype.closeDrawer = function () {
    if (this.dom.drawer) this.dom.drawer.classList.remove('on');
    if (this.dom.drawerVeil) this.dom.drawerVeil.classList.remove('on');
  };

  /* ---------------- step 3 ---------------- */

  BYSKv2.prototype.bindSchedule = function () {
    var self = this;
    Array.prototype.forEach.call(this.dom.schedPills, function (b) {
      b.addEventListener('click', function () {
        self.state.weeks = b.dataset.w;
        self.render();
      });
    });
  };

  /* Selling plan for a given scent + the shared frequency. Subscription-only,
     like the control — the discount function also requires the plan to fire. */
  BYSKv2.prototype.sellingPlanFor = function (s) {
    if (!s || !s.plans) return null;
    return s.plans[this.state.weeks] || null;
  };

  /* Derived on every render, never stored — a stale total is a support ticket.
     Matches the control: discount on the diffuser, scent comped. */
  BYSKv2.prototype.totals = function () {
    var d = this.state.diffuser_variant;
    var qty = this.state.qty;
    var picked = this.pickedScents();
    var unit = d ? d.price : 0;
    var unit_now = Math.round(unit * (1 - DATA.discount_pct / 100));

    /* one fragrance value per bottle: picked price where known, the cheapest
       otherwise — mirrors the per-kit "value" story on the page */
    var scent_display = 0;
    for (var i = 0; i < qty; i++) {
      scent_display += this.state.scents[i] ? this.state.scents[i].price : minScentPrice();
    }

    var total = unit_now * qty;
    if (!DATA.free_scent) {
      picked.forEach(function (x) { total += x.price; });
    }
    var msrp = unit * qty + scent_display;

    return {
      unit: unit,
      unit_now: unit_now,
      total: total,
      msrp: msrp,
      save: msrp - total
    };
  };

  BYSKv2.prototype.render = function () {
    var self = this;
    var t = this.totals();
    /* the fragrance belonging to the bottle currently being picked for */
    var s = this.state.scents[this.state.picking] || null;

    Array.prototype.forEach.call(this.dom.panels, function (p) {
      p.classList.toggle('on', parseInt(p.dataset.panel, 10) === self.state.step);
    });
    Array.prototype.forEach.call(this.dom.psteps, function (ps) {
      var n = parseInt(ps.dataset.step, 10);
      ps.classList.toggle('active', n === self.state.step);
      ps.classList.toggle('done', n < self.state.step);
      ps.classList.toggle('clickable', n < self.state.step);
      var dot = ps.querySelector('.dot');
      if (dot) dot.textContent = n < self.state.step ? '✓' : n;
    });

    /* step 1 */
    Array.prototype.forEach.call(this.dom.diffuserOpts, function (el) {
      var sel = self.state.diffuser_variant;
      el.classList.toggle('sel', !!sel && String(sel.id) === el.dataset.diffuserVariant);
    });
    if (this.dom.qtyVal) this.dom.qtyVal.textContent = this.state.qty;
    if (this.dom.qtyDec) this.dom.qtyDec.disabled = this.state.qty <= 1;
    if (this.dom.qtyInc) this.dom.qtyInc.disabled = this.state.qty >= DATA.max_qty;
    if (this.dom.stackDiffT && this.state.diffuser_variant) {
      this.dom.stackDiffT.textContent = this.state.qty + '× ' + DATA.diffuser.title;
    }
    /* The kit list showed the product's featured image regardless of the colour
       picked — pick Black, still see the White unit. The step-3 summary already
       swapped; step 1 never did. */
    if (this.dom.stackDiffImg && this.state.diffuser_variant && this.state.diffuser_variant.image) {
      this.dom.stackDiffImg.src = this.state.diffuser_variant.image;
    }
    if (this.dom.stackDiffV) {
      this.dom.stackDiffV.innerHTML =
        '<span class="was">' + money(t.unit * this.state.qty) + '</span>' +
        money(t.unit_now * this.state.qty);
    }
    if (this.dom.stackTotal) this.dom.stackTotal.textContent = money(t.total);
    if (this.dom.savePill) {
      this.dom.savePill.textContent = DATA.labels.save + ' ' + money(t.save);
    }
    this.renderKitScentRow();

    /* step 2 — copy pluralizes with quantity */
    if (this.dom.step2HeadingText && DATA.labels.step_2_heading_multi) {
      this.dom.step2HeadingText.textContent = this.state.qty > 1
        ? DATA.labels.step_2_heading_multi
        : DATA.labels.step_2_heading;
    }
    if (this.dom.freeNote && DATA.labels.free_note_multi) {
      this.dom.freeNote.textContent = this.state.qty > 1
        ? fill(DATA.labels.free_note_multi, '', '', this.state.qty)
        : DATA.labels.free_note;
    }

    /* step 2 — chips for the bottles, every picked fragrance highlighted;
       a card picked for several bottles carries a ×N badge instead of a tick */
    this.renderKitPicker();
    var counts = {};
    this.pickedScents().forEach(function (x) {
      var key = String(x.id);
      counts[key] = (counts[key] || 0) + 1;
    });
    Array.prototype.forEach.call(this.dom.scentCards, function (el) {
      var n = counts[el.dataset.scentVariant] || 0;
      /* Eligibility is re-derived on every render: the selected frequency
         can change on step 3, and a scent without that plan must fall back
         to "sold out" rather than cart without one. */
      var eligible = self.scentEligible(self.findScent(el.dataset.scentVariant));
      el.dataset.available = eligible ? 'true' : 'false';
      el.classList.toggle('soldout', !eligible);
      el.classList.toggle('sel', n > 0);
      el.classList.toggle('multi', n > 1);
      var cm = el.querySelector('[data-countmark]');
      if (cm) cm.textContent = '\u00d7' + n;
    });

    /* step 3 */
    Array.prototype.forEach.call(this.dom.schedPills, function (b) {
      b.classList.toggle('on', b.dataset.w === self.state.weeks);
    });
    this.renderSummary(t);
    this.renderSticky(t);
  };

  /* Step 2's chip row — one chip per bottle, only when there is more than
     one. The active chip is the bottle the next grid tap assigns to; tapping
     a chip re-targets it (so a customer can change bottle 1's pick after
     bottle 2). Built with textContent — titles come from Shopify. */
  BYSKv2.prototype.renderKitPicker = function () {
    var el = this.dom.kitPicker;
    if (!el) return;
    var self = this;
    var n = this.state.qty;
    if (n < 2) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    el.innerHTML = '';
    for (var i = 0; i < n; i++) {
      (function (i) {
        var scent = self.state.scents[i];
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'kp-chip' + (scent ? ' done' : '') +
          (i === self.state.picking ? ' active' : '');
        var dot = document.createElement('span');
        dot.className = 'dot-sm';
        dot.style.background = scent && scent.accent ? scent.accent : 'var(--cream-deep)';
        chip.appendChild(dot);
        chip.appendChild(document.createTextNode(
          (DATA.labels.kit_label || 'Bottle') + ' ' + (i + 1) + ': ' +
          (scent ? scent.title + ' ' : '\u2026')
        ));
        if (scent) {
          var tick = document.createElement('span');
          tick.className = 'tick';
          tick.textContent = '\u2713';
          chip.appendChild(tick);
        }
        chip.addEventListener('click', function () {
          self.state.picking = i;
          self.render();
        });
        el.appendChild(chip);
      })(i);
    }
  };

  /* The step-1 kit list must never look settled before every bottle has a
     fragrance: the row stays unchecked until step 2 fills them all (a
     customer can navigate back to step 1 afterwards). For multi-bottle it
     speaks in counts: "2 free fragrances". */
  BYSKv2.prototype.renderKitScentRow = function () {
    var d = this.dom;
    if (!d.stackScentRow) return;
    var kits = this.state.qty;
    var picked = this.pickedScents();
    var chosen = kits > 1 ? this.allScentsPicked() : !!this.state.scents[0];

    d.stackScentRow.classList.toggle('is-done', chosen);
    d.stackScentRow.classList.toggle('is-pending', !chosen);

    if (d.stackScentName) {
      if (kits > 1) {
        d.stackScentName.textContent = chosen
          ? fill(DATA.labels.scents_done_title_multi, '', '', kits)
          : fill(DATA.labels.scent_pending_title_multi, '', '', kits);
      } else {
        d.stackScentName.textContent = this.state.scents[0]
          ? fill(DATA.labels.scent_sum_title, this.state.scents[0].title, '')
          : DATA.labels.scent_pending_title;
      }
    }
    if (d.stackScentD) {
      d.stackScentD.textContent = kits > 1 && picked.length
        ? picked.map(function (x) { return x.title; }).join(' + ')
        : (chosen ? DATA.labels.scent_blurb : DATA.labels.scent_pending_blurb);
    }
    if (d.stackScentImg) {
      var img = this.state.scents[0];
      if (img && img.image) d.stackScentImg.src = img.image;
      d.stackScentImg.hidden = !this.state.scents[0];
    }
    /* free value: picked bottles at their price, unpicked at the cheapest */
    if (d.stackScentV) {
      var val = 0;
      for (var i = 0; i < kits; i++) {
        val += this.state.scents[i] ? this.state.scents[i].price : minScentPrice();
      }
      d.stackScentV.innerHTML = '<span class="was">' + money(val) + '</span>FREE';
    }
  };

  /* Step-3 summary: the diffuser row, then one aggregated row per fragrance
     — 2× Bliss reads as one line, matching the cart shape (one line per scent
     variant). Rows are rebuilt each render into [data-sum-scents]; escaping
     goes through textContent, never string-concat. */
  BYSKv2.prototype.renderSummary = function (t) {
    var d = this.dom;
    var dv = this.state.diffuser_variant;

    if (d.sumDiffT && dv) {
      d.sumDiffT.textContent = this.state.qty + '× ' + DATA.diffuser.title + ' — ' + dv.title;
    }
    if (d.sumDiffImg && dv && dv.image) d.sumDiffImg.src = dv.image;
    if (d.sumDiffV) {
      d.sumDiffV.innerHTML =
        '<span class="was">' + money(t.unit * this.state.qty) + '</span>' +
        money(t.unit_now * this.state.qty);
    }

    if (d.sumScents) {
      d.sumScents.innerHTML = '';
      var order = [];
      var byId = {};
      this.pickedScents().forEach(function (x) {
        var key = String(x.id);
        if (!byId[key]) { byId[key] = { scent: x, n: 0 }; order.push(key); }
        byId[key].n += 1;
      });
      order.forEach(function (key) {
        var entry = byId[key];
        var x = entry.scent;
        var el = document.createElement('div');
        el.className = 'stack-row';
        var sq = document.createElement('div');
        sq.className = 'sq';
        if (x.image) {
          var img = document.createElement('img');
          img.src = x.image; img.alt = ''; img.width = 75; img.height = 75;
          sq.appendChild(img);
        }
        var grow = document.createElement('div');
        grow.className = 'grow';
        var tEl = document.createElement('div');
        tEl.className = 't';
        tEl.textContent = fill(
          DATA.labels.scent_sum_title,
          (entry.n > 1 ? entry.n + '\u00d7 ' : '') + x.title,
          ''
        );
        var dEl = document.createElement('div');
        dEl.className = 'd';
        dEl.textContent = x.insp
          ? DATA.labels.inspired_by + ' ' + x.insp + ' \u00b7 100ml'
          : DATA.labels.scent_blurb;
        grow.appendChild(tEl);
        grow.appendChild(dEl);
        var val = document.createElement('div');
        val.className = 'val free';
        val.innerHTML = '<span class="was">' + money(x.price * entry.n) + '</span>FREE';
        el.appendChild(sq); el.appendChild(grow); el.appendChild(val);
        d.sumScents.appendChild(el);
      });
    }

    /* refill line stays per-bottle — the copy around it reads "per refill".
       Mixed picks with different refill prices show "from" the cheapest. */
    if (d.refillPrice) {
      var prices = [];
      this.pickedScents().forEach(function (x) {
        var pct = x.refill_pct || 10;
        var rp = Math.round(x.price * (1 - pct / 100));
        if (prices.indexOf(rp) === -1) prices.push(rp);
      });
      if (prices.length) {
        d.refillPrice.textContent =
          (prices.length === 1
            ? money(prices[0])
            : DATA.labels.from_prefix + ' ' + money(Math.min.apply(null, prices))) +
          ' every ' + this.state.weeks + ' weeks';
      }
    }

    if (d.sumValueTag) d.sumValueTag.textContent = money(t.msrp) + ' ' + DATA.labels.value_suffix;
    if (d.sumTotal) d.sumTotal.textContent = money(t.total);
    if (d.sumSavePill) d.sumSavePill.textContent = DATA.labels.save + ' ' + money(t.save);
  };

  BYSKv2.prototype.renderSticky = function (t) {
    var d = this.dom;
    if (d.stickyWas) d.stickyWas.textContent = t.msrp > t.total ? money(t.msrp) : '';
    if (d.stickyNow) d.stickyNow.textContent = money(t.total);
    /* The collapsed mobile bar shows a different second row per step — the note
       on step 1, the total on steps 2-3 — so it has to advertise which step it
       is on. */
    if (d.stickyRoot) d.stickyRoot.setAttribute('data-step', this.state.step);
    if (!d.stickyCta) return;
    var giftStep = DATA.fragrance_gift_cta === true && this.state.step === 2;
    d.stickyCta.classList.toggle('cta--fragrance-gift', giftStep);
    d.stickyCta.removeAttribute('aria-label');

    if (this.state.step === 1) {
      d.stickyCta.disabled = false;
      d.stickyCta.textContent = DATA.labels.cta_step_1;
      if (d.stickyLbl) d.stickyLbl.textContent = DATA.labels.total_step_1;
      if (d.stickyNote) d.stickyNote.textContent = DATA.labels.note_step_1;
    } else if (this.state.step === 2) {
      var kits = this.state.qty;
      if (this.allScentsPicked()) {
        d.stickyCta.disabled = false;
        d.stickyCta.textContent = kits > 1
          ? fill(DATA.labels.cta_step_2_multi, '', '', kits)
          : fill(DATA.labels.cta_step_2, this.state.scents[0].title, '');
      } else {
        d.stickyCta.disabled = !giftStep;
        d.stickyCta.textContent = kits > 1
          ? fill(DATA.labels.cta_step_2_pick_for, '', '', this.nextUnpicked() + 1)
          : DATA.labels.cta_step_2_empty;
      }
      if (d.stickyLbl) d.stickyLbl.textContent = DATA.labels.total_step_1;
      if (d.stickyNote) d.stickyNote.textContent = DATA.labels.note_step_2;
    } else {
      d.stickyCta.disabled = false;
      d.stickyCta.textContent = DATA.labels.checkout;
      if (d.stickyLbl) d.stickyLbl.textContent = DATA.labels.total_step_3;
      if (d.stickyNote) d.stickyNote.textContent = DATA.labels.note_step_3;
    }
    if (giftStep) {
      var label = d.stickyCta.textContent.replace(/\s*→\s*$/, '').toUpperCase();
      d.stickyCta.setAttribute('aria-label', label);
      d.stickyCta.innerHTML = '<svg class="cta-gift" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10h16v11H4zM3 6h18v4H3zM12 6v15"/><path d="M12 6H8.5A2.5 2.5 0 1 1 11 3.5L12 6Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5L12 6Z"/></svg><span class="cta-divider" aria-hidden="true"></span><span class="cta-label"></span><svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h16m-7-7 7 7-7 7"/></svg>';
      d.stickyCta.querySelector('.cta-label').textContent = label;
    }
  };

  BYSKv2.prototype.bindStickyCta = function () {
    var self = this;
    if (!this.dom.stickyCta) return;
    this.dom.stickyCta.addEventListener('click', function () {
      if (self.state.step === 1) {
        self.goToStep(2);
      } else if (self.state.step === 2) {
        if (self.allScentsPicked()) self.goToStep(3);
        else if (DATA.fragrance_gift_cta === true) self.focusFragranceChoices();
      } else {
        self.checkout();
      }
    });
  };

  BYSKv2.prototype.focusFragranceChoices = function () {
    this.state.picking = this.nextUnpicked();
    this.renderKitPicker();
    var target = Array.prototype.find.call(this.dom.scentCards, function (card) {
      return card.dataset.available === 'true' && card.getBoundingClientRect().height > 0;
    });
    if (!target) target = this.root.querySelector('.filters');
    if (!target) return;
    var header = this.root.querySelector('header.site');
    var progress = this.root.querySelector('.progress-wrap');
    var offset = (header ? header.getBoundingClientRect().height : 0) +
      (progress ? progress.getBoundingClientRect().height : 0) + 16;
    var top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    window.scrollTo({ top: top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  /* Shared sink so both arms emit identical events —
     scentify-analytics.js owns the GrowthBook/Mixpanel fan-out. */
  BYSKv2.prototype.track = function (event, props) {
    if (window.ScentifyAnalytics && typeof window.ScentifyAnalytics.track === 'function') {
      window.ScentifyAnalytics.track(event, props || {});
    }
  };

  /* Every click starts a FRESH checkout: clear the cart first so backing out
     of checkout and clicking again can never double the items — the customer
     checks out with exactly what the summary showed, every time. */
  BYSKv2.prototype.checkout = async function () {
    var self = this;
    var d = this.state.diffuser_variant;
    if (!d || !this.allScentsPicked()) return;

    /* Fail closed before cart/clear: a pick that has lost its refill plan
       (frequency changed on step 3, catalog edited mid-session) must never
       reach checkout without the discount contract. */
    var picked = this.pickedScents();
    for (var i = 0; i < picked.length; i++) {
      if (!this.sellingPlanFor(picked[i])) {
        console.error('[bysk-v2] selected fragrance is missing its refill plan');
        this.render();
        return;
      }
    }

    var btn = this.dom.stickyCta;
    btn.classList.add('active');
    btn.setAttribute('disabled', 'disabled');

    /* Closes the funnel: step 3 viewed -> checkout clicked. Fired before the
       cart calls so a cart/add failure still shows up as intent. */
    this.track('bysk_checkout_clicked', this.stepProps(3, 'checkout'));

    /* The reduction is applied by a discount function that keys off these
       line-item properties — same contract as the live control
       (component-build-your-starter-kit-multi.js): '_starter-kit' comps the
       scent ("FREE SCENT"), the extra property (default '_forty-off') takes
       40% off the diffuser ("40% OFF DIFFUSER"). Both fire only when the
       scent carries a selling plan. One comped bottle per diffuser: one line
       per scent variant with quantity = bottles using it — the exact shape
       v4 and the old multi-set builder already ship. */
    var diffuserItem = {
      id: parseInt(d.id, 10),
      quantity: this.state.qty,
      properties: { '_starter-kit': 'true' }
    };
    if (DATA.diffuser_extra_property) {
      diffuserItem.properties[DATA.diffuser_extra_property] = 'true';
    }

    /* aggregate the per-bottle picks by scent variant */
    var items = [diffuserItem];
    var scentQty = {};
    var scentOrder = [];
    this.pickedScents().forEach(function (x) {
      var key = String(x.id);
      if (!scentQty[key]) { scentQty[key] = { scent: x, qty: 0 }; scentOrder.push(key); }
      scentQty[key].qty += 1;
    });
    scentOrder.forEach(function (key) {
      var entry = scentQty[key];
      var item = { id: parseInt(entry.scent.id, 10), quantity: entry.qty };
      var plan = self.sellingPlanFor(entry.scent);
      if (plan) item.selling_plan = parseInt(plan, 10);
      items.push(item);
    });

    var formData = { items: items };

    /* No explicit product_added_to_cart here: scentify-analytics.js
       intercepts cart/add.js and fires it per item on success — the same
       instrumentation the control gets. An explicit call would double-count
       adds on this arm only. */

    try {
      var root = window.Shopify.routes.root;
      await fetch(root + 'cart/clear.js', { method: 'POST' });
      var res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('cart/add failed: ' + res.status);
      await res.json();

      if (window.ScentifyAnalytics && window.ScentifyAnalytics.syncCartAttributes) {
        await window.ScentifyAnalytics.syncCartAttributes();
      }
      window.location.href = '/checkout';
    } catch (err) {
      console.error('[bysk-v2]', err);
      btn.classList.remove('active');
      btn.removeAttribute('disabled');
      this.render();
    }
  };

  function initializeAssignedBuilder() {
    var decision = window.__scentifyStarterDecision;
    if (decision && (!decision.ready || decision.arm !== 'control')) return;
    var root = document.querySelector('[data-bysk-v2][data-bysk-version="v5-control"]');
    if (root && !root.__byskInitialized) {
      root.__byskInitialized = true;
      var previewBuilder = new BYSKv2(root);
      if (window.__scentifyFragrancePreview === true || (window.location && window.location.search && new URLSearchParams(window.location.search).has('notrack') && new URLSearchParams(window.location.search).get('preview_step') === 'fragrance')) {
        previewBuilder.goToStep(2);
      }
    }
  }
  document.addEventListener('DOMContentLoaded', initializeAssignedBuilder);
  document.addEventListener('scentify:starter-assigned', initializeAssignedBuilder);
})();
